export type AvatarColor = 'brand' | 'tech' | 'legal' | 'orc';
export type AvatarSize = 'xs' | 'sm' | 'default';

interface AvatarProps {
  /** Nome completo (usado para tooltip + extração de iniciais se `iniciais` ausente) */
  nome: string;
  /** Iniciais a exibir (override). Se ausente, deriva do `nome` (até 2 letras) */
  iniciais?: string;
  /** Cor do avatar baseada no papel da pessoa */
  cor?: AvatarColor;
  size?: AvatarSize;
  /** Borda usada quando avatar está em AvatarStack (sobreposto) */
  stacked?: boolean;
}

const COR_CLASSES: Record<AvatarColor, string> = {
  brand: 'bg-[var(--brand-600)]',
  tech: 'bg-[#0891B2]',
  legal: 'bg-[#7C3AED]',
  orc: 'bg-[#D97706]',
};

const SIZE_CLASSES: Record<AvatarSize, string> = {
  xs: 'size-[18px] text-[9px]',
  sm: 'size-[22px] text-[10px]',
  default: 'size-7 text-[11px]',
};

function extrairIniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/);
  if (partes.length === 0) return '?';
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
}

/**
 * Avatar atômico do design system com 4 cores semânticas.
 *
 * @example
 * <Avatar nome="Marcos Filho" cor="brand" />
 * <Avatar nome="Ana Silva" cor="tech" size="sm" />
 */
export default function Avatar({
  nome,
  iniciais,
  cor = 'brand',
  size = 'default',
  stacked = false,
}: AvatarProps) {
  const letras = iniciais ?? extrairIniciais(nome);
  const corClass = COR_CLASSES[cor];
  const sizeClass = SIZE_CLASSES[size];
  const stackedClass = stacked ? '-ml-1.5 first:ml-0 border-2 border-[var(--bg-card)]' : '';

  return (
    <span
      className={`inline-flex items-center justify-center rounded-full font-semibold text-white shrink-0 ${corClass} ${sizeClass} ${stackedClass}`}
      title={nome}
      aria-label={nome}
    >
      {letras}
    </span>
  );
}
